<template>
  <div class="survey-page">
    <div class="container">
      <NavigationBar />
    </div>

    <div class="container survey-container">
      <!-- Thank-you state -->
      <div v-if="submitted" class="survey-card survey-thankyou">
        <div class="thankyou-emoji">💜</div>
        <h1 class="thankyou-title">{{ c.thankYouTitle }}</h1>
        <p class="thankyou-body">{{ c.thankYouBody }}</p>
      </div>

      <!-- Form -->
      <form v-else class="survey-form" novalidate @submit.prevent="submit">
        <div class="survey-card survey-header">
          <h1 class="survey-title">{{ c.pageTitle }}</h1>
          <p class="survey-subtitle">{{ c.pageSubtitle }}</p>
          <p class="survey-time">{{ c.introTime }}</p>
          <p class="survey-required-note">{{ c.requiredNote }}</p>
        </div>

        <div v-if="!hasUid" class="survey-card survey-notice" role="alert">
          {{ c.linkInvalid }}
        </div>

        <template v-for="(section, sIndex) in c.sections" :key="sIndex">
          <!-- Section intro -->
          <div v-if="section.intro" class="survey-card survey-section-intro">
            <h2 class="section-intro-title">{{ section.intro.title }}</h2>
            <p class="section-intro-body">{{ section.intro.body }}</p>
          </div>

          <!-- Questions -->
          <div
            v-for="q in section.questions"
            :id="'q-' + q.id"
            :key="q.id"
            class="survey-card question"
            :class="{ 'question--error': errors[q.id] }"
          >
            <p class="question__label">
              {{ q.label }}
              <span v-if="q.required" class="question__required">*</span>
            </p>
            <p v-if="q.help" class="question__help">{{ q.help }}</p>

            <!-- Single choice -->
            <div
              v-if="q.type === 'single'"
              class="options"
              role="radiogroup"
              :aria-required="q.required"
              :aria-invalid="!!errors[q.id]"
            >
              <button
                v-for="opt in displayOptions(q)"
                :key="opt.value"
                type="button"
                class="option"
                role="radio"
                :aria-checked="answers[q.id] === opt.value"
                :class="{ 'option--selected': answers[q.id] === opt.value }"
                @click="selectSingle(q, opt.value)"
              >
                <span class="option__radio"></span>
                <span class="option__label">{{ opt.label }}</span>
              </button>
            </div>

            <!-- Multiple choice -->
            <div
              v-else-if="q.type === 'multi'"
              class="options"
              role="group"
              :aria-invalid="!!errors[q.id]"
            >
              <button
                v-for="opt in displayOptions(q)"
                :key="opt.value"
                type="button"
                class="option"
                role="checkbox"
                :aria-checked="isFeatureSelected(opt.value)"
                :class="{
                  'option--selected': isFeatureSelected(opt.value),
                  'option--disabled': isFeatureDisabled(opt.value),
                }"
                :disabled="isFeatureDisabled(opt.value)"
                @click="toggleFeature(opt.value)"
              >
                <span class="option__check"></span>
                <span class="option__label">{{ opt.label }}</span>
              </button>

              <!-- Other -->
              <div v-if="q.allowOther" class="option-other">
                <button
                  type="button"
                  class="option"
                  role="checkbox"
                  :aria-checked="isFeatureSelected('other')"
                  :class="{
                    'option--selected': isFeatureSelected('other'),
                    'option--disabled': isFeatureDisabled('other'),
                  }"
                  :disabled="isFeatureDisabled('other')"
                  @click="toggleFeature('other')"
                >
                  <span class="option__check"></span>
                  <span class="option__label">{{ c.otherPlaceholder }}</span>
                </button>
                <input
                  v-if="isFeatureSelected('other')"
                  v-model.trim="answers.desiredFeaturesOther"
                  class="other-input"
                  type="text"
                  :placeholder="c.otherPlaceholder"
                  :aria-label="c.otherPlaceholder"
                  maxlength="500"
                />
              </div>
            </div>

            <!-- Scale (0–10 or 1–5) -->
            <div
              v-else-if="q.type === 'scale'"
              class="scale"
              role="radiogroup"
              :aria-required="q.required"
              :aria-invalid="!!errors[q.id]"
            >
              <div v-if="q.lowLabel || q.highLabel" class="scale__legend">
                <p v-if="q.lowLabel" class="scale__legend-item">{{ q.min }} = {{ q.lowLabel }}</p>
                <p v-if="q.highLabel" class="scale__legend-item">{{ q.max }} = {{ q.highLabel }}</p>
              </div>
              <div class="scale__numbers">
                <button
                  v-for="n in scaleRange(q)"
                  :key="n"
                  type="button"
                  class="scale__number"
                  role="radio"
                  :aria-checked="answers[q.id] === n"
                  :aria-label="String(n)"
                  :class="{ 'scale__number--selected': answers[q.id] === n }"
                  @click="answers[q.id] = n"
                >
                  {{ n }}
                </button>
              </div>
            </div>

            <p v-if="errors[q.id]" class="question__error" role="alert">{{ errors[q.id] }}</p>
          </div>
        </template>

        <div class="survey-card survey-submit-card">
          <p v-if="submitError" class="submit-error" role="alert">{{ submitError }}</p>
          <button type="submit" class="submit-btn" :disabled="submitting || !hasUid">
            {{ submitting ? c.submitting : c.submit }}
          </button>
        </div>
      </form>
    </div>

    <FooterComponent />
  </div>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import NavigationBar from "@/components/NavigationBar.vue";
import FooterComponent from "@/components/Components/FooterComponent.vue";
import { apiBaseUrl } from "@/config/api";
import {
  surveyFeedbackContent,
  SURVEY_KEY,
  REQUIRED_QUESTIONS,
  MAX_DESIRED_FEATURES as MAX_FEATURES,
} from "@/i18n/surveyFeedback";

const route = useRoute();
const { locale } = useI18n();

const lang = computed(() => (locale.value === "en" ? "en" : "bg"));
const c = computed(() => surveyFeedbackContent[lang.value]);

const uid = computed(() => {
  const v = route.query.uid;
  return typeof v === "string" && v ? v : null;
});
const hasUid = computed(() => !!uid.value);
const surveyKey = computed(() => {
  const v = route.query.survey;
  return typeof v === "string" && v ? v : SURVEY_KEY;
});

const answers = reactive({
  ayaSupport: null,
  recommend: null,
  desiredFeatures: [],
  desiredFeaturesOther: "",
  hospitalVisitCount: null,
  lastVisit: null,
  anxietyDuringVisit: null,
  anxietyNow: null,
  hospitalSupport: null,
  satisfactionVisits: null,
  satisfactionMomfidentAccess: null,
});

const errors = reactive({});
const submitting = ref(false);
const submitted = ref(false);
const submitError = ref("");

function scaleRange(q) {
  const out = [];
  for (let n = q.min; n <= q.max; n++) out.push(n);
  return out;
}

// --- Option shuffling: text/choice answers reshuffle on each page load;
// scales (0–10, 1–5) keep their natural order. ---
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// A per-load random order of option VALUES for each single/multi question.
// Built once per mount, so the order stays stable while filling but reshuffles
// on reload. Keyed by value so it survives a language switch.
const optionOrder = {};
for (const section of surveyFeedbackContent.bg.sections) {
  for (const q of section.questions) {
    if (q.type === "single" || q.type === "multi") {
      optionOrder[q.id] = shuffle(q.options.map((o) => o.value));
    }
  }
}

function displayOptions(q) {
  const order = optionOrder[q.id];
  if (!order) return q.options;
  const byValue = new Map(q.options.map((o) => [o.value, o]));
  return order.map((v) => byValue.get(v)).filter(Boolean);
}

// --- Single choice ---
function selectSingle(q, value) {
  // Allow deselecting an optional single-choice answer by clicking it again.
  if (!q.required && answers[q.id] === value) {
    answers[q.id] = null;
  } else {
    answers[q.id] = value;
  }
  delete errors[q.id];
}

// --- Multi choice (desiredFeatures) ---
function isFeatureSelected(value) {
  return answers.desiredFeatures.includes(value);
}

function isFeatureDisabled(value) {
  if (isFeatureSelected(value)) return false;
  // "None of the above" is exclusive.
  if (answers.desiredFeatures.includes("none")) return value !== "none";
  // Max selections reached.
  return answers.desiredFeatures.length >= MAX_FEATURES;
}

function toggleFeature(value) {
  const arr = answers.desiredFeatures;
  const idx = arr.indexOf(value);
  if (idx >= 0) {
    arr.splice(idx, 1);
    if (value === "other") answers.desiredFeaturesOther = "";
    delete errors.desiredFeatures;
    return;
  }
  if (value === "none") {
    answers.desiredFeatures = ["none"];
    answers.desiredFeaturesOther = "";
    delete errors.desiredFeatures;
    return;
  }
  const withoutNone = arr.filter((v) => v !== "none");
  if (withoutNone.length >= MAX_FEATURES) return;
  answers.desiredFeatures = [...withoutNone, value];
  delete errors.desiredFeatures;
}

// --- Validation & submit ---
function validate() {
  Object.keys(errors).forEach((k) => delete errors[k]);

  for (const id of REQUIRED_QUESTIONS) {
    const v = answers[id];
    if (v === null || v === undefined || v === "") {
      errors[id] = c.value.errorRequired;
    }
  }
  if (
    answers.desiredFeatures.includes("other") &&
    !answers.desiredFeaturesOther.trim()
  ) {
    errors.desiredFeatures = c.value.errorOther;
  }
  return Object.keys(errors).length === 0;
}

function scrollToFirstError() {
  const ordered = c.value.sections.flatMap((s) => s.questions.map((q) => q.id));
  const firstId = ordered.find((id) => errors[id]);
  if (!firstId) return;
  const el = document.getElementById("q-" + firstId);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}

function buildAnswers() {
  const a = {
    hospitalVisitCount: answers.hospitalVisitCount,
    lastVisit: answers.lastVisit,
    anxietyDuringVisit: answers.anxietyDuringVisit,
    anxietyNow: answers.anxietyNow,
    hospitalSupport: answers.hospitalSupport,
    satisfactionVisits: answers.satisfactionVisits,
    satisfactionMomfidentAccess: answers.satisfactionMomfidentAccess,
  };
  if (answers.ayaSupport) a.ayaSupport = answers.ayaSupport;
  if (answers.recommend) a.recommend = answers.recommend;
  if (answers.desiredFeatures.length) {
    a.desiredFeatures = [...answers.desiredFeatures];
    if (
      answers.desiredFeatures.includes("other") &&
      answers.desiredFeaturesOther.trim()
    ) {
      a.desiredFeaturesOther = answers.desiredFeaturesOther.trim();
    }
  }
  return a;
}

async function submit() {
  submitError.value = "";
  // The backend requires a valid user id from the link; don't even try without one.
  if (!hasUid.value) return;
  if (!validate()) {
    scrollToFirstError();
    return;
  }

  submitting.value = true;
  try {
    const payload = {
      locale: lang.value,
      answers: buildAnswers(),
    };
    if (uid.value) payload.userId = uid.value;

    // Cross-origin call to the backend (different domain). `mode: "cors"` is
    // explicit; we deliberately do NOT send credentials so the backend's
    // permissive CORS (reflected origin + wildcard headers) applies cleanly.
    const res = await fetch(
      `${apiBaseUrl}/api/surveys/${encodeURIComponent(surveyKey.value)}/responses`,
      {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      }
    );
    if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

    submitted.value = true;
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (err) {
    console.error("Survey submit failed:", err);
    submitError.value = c.value.errorSubmit;
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables.scss";

.survey-page {
  font-family: $font-regular;
  color: $color-purple;
  background: $color-pink-lighter;
  min-height: 100vh;
  overflow-x: hidden;
}

.survey-container {
  max-width: 760px;
  padding-bottom: 64px;
}

.survey-card {
  background: #ffffff;
  border-radius: 18px;
  padding: 28px 32px;
  margin-bottom: 18px;
  box-shadow: 0 6px 22px rgba(107, 107, 156, 0.08);
}

/* Header */
.survey-header {
  border-top: 6px solid $color-pink-dark-2;
}

.survey-title {
  font-family: $font-semi-bold;
  font-size: 26px;
  line-height: 1.25;
  color: $color-purple;
  margin: 0 0 10px;
}

.survey-subtitle {
  font-size: 16px;
  color: $color-pink-dark;
  margin: 0 0 14px;
}

.survey-time {
  font-size: 14px;
  color: lighten($color-purple, 12%);
  margin: 0 0 6px;
}

.survey-required-note {
  font-size: 13px;
  color: $color-pink-dark-2;
  margin: 0;
}

.survey-notice {
  border-left: 4px solid $color-pink-dark-2;
  background: $color-pink-light;
  box-shadow: none;
  color: $color-purple;
  font-size: 14px;
  line-height: 1.5;
}

/* Section intro */
.survey-section-intro {
  background: $color-pink-light;
  box-shadow: none;
}

.section-intro-title {
  font-family: $font-semi-bold;
  font-size: 18px;
  color: $color-purple;
  margin: 0 0 8px;
}

.section-intro-body {
  font-size: 15px;
  line-height: 1.5;
  margin: 0;
}

/* Question */
.question {
  transition: border-color 0.2s ease;
  border: 1px solid transparent;
}

.question--error {
  border-color: rgba(252, 103, 139, 0.6);
}

.question__label {
  font-family: $font-semi-bold;
  font-size: 16px;
  line-height: 1.4;
  color: $color-purple;
  margin: 0 0 4px;
}

.question__required {
  color: $color-pink-dark-2;
  margin-left: 2px;
}

.question__help {
  font-size: 13px;
  color: lighten($color-purple, 15%);
  margin: 0 0 12px;
}

.question__error {
  margin: 12px 0 0;
  font-size: 13px;
  color: #e0356b;
}

/* Options (radio / checkbox) */
.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1.5px solid #ece9f3;
  border-radius: 12px;
  padding: 12px 16px;
  font-family: $font-regular;
  font-size: 15px;
  color: $color-purple;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(.option--disabled):not(.option--selected) {
    border-color: $color-pink-dark;
    background: $color-pink-lighter;
  }
}

.option--selected {
  border-color: $color-pink-dark;
  background: $color-pink-light;
}

.option--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.option__radio,
.option__check {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border: 2px solid #cfc9de;
  background: #fff;
  transition: all 0.15s ease;
}

.option__radio {
  border-radius: 50%;
}

.option__check {
  border-radius: 6px;
}

.option--selected .option__radio {
  border-color: $color-pink-dark;
  box-shadow: inset 0 0 0 4px $color-pink-dark;
}

.option--selected .option__check {
  border-color: $color-pink-dark;
  background: $color-pink-dark;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 1px;
    width: 5px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
}

.option__label {
  flex: 1;
}

/* Other free-text */
.option-other {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.other-input {
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #ece9f3;
  border-radius: 12px;
  font-family: $font-regular;
  font-size: 15px;
  color: $color-purple;

  &:focus {
    outline: none;
    border-color: $color-pink-dark;
    box-shadow: 0 0 0 3px rgba(234, 112, 140, 0.15);
  }
}

/* Scale */
.scale {
  margin-top: 16px;
}

.scale__legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 0 12px;
}

.scale__legend-item {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: lighten($color-purple, 12%);
  text-align: left;
}

.scale__numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scale__number {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid #ece9f3;
  background: #fff;
  font-family: $font-semi-bold;
  font-size: 15px;
  color: $color-purple;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(.scale__number--selected) {
    border-color: $color-pink-dark;
    background: $color-pink-lighter;
  }
}

.scale__number--selected {
  border-color: $color-pink-dark;
  background: $color-pink-dark;
  color: #fff;
}

/* Submit */
.survey-submit-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
}

.submit-error {
  margin: 0;
  font-size: 14px;
  color: #e0356b;
}

.submit-btn {
  background: linear-gradient(135deg, $color-pink-dark 0%, darken($color-pink-dark, 10%) 100%);
  color: #fff;
  border: none;
  border-radius: 25px;
  padding: 14px 40px;
  font-family: $font-semi-bold;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(234, 112, 140, 0.3);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(234, 112, 140, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* Thank you */
.survey-thankyou {
  text-align: center;
  padding: 56px 32px;
  border-top: 6px solid $color-pink-dark-2;
}

.thankyou-emoji {
  font-size: 48px;
  margin-bottom: 12px;
}

.thankyou-title {
  font-family: $font-semi-bold;
  font-size: 24px;
  color: $color-purple;
  margin: 0 0 14px;
}

.thankyou-body {
  font-size: 16px;
  line-height: 1.55;
  color: lighten($color-purple, 8%);
  max-width: 520px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .survey-container {
    padding-left: 14px;
    padding-right: 14px;
  }

  .survey-card {
    padding: 22px 18px;
  }

  .survey-title {
    font-size: 22px;
  }

  .scale__number {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }
}
</style>
